import {
  number,
  priceAfterTax,
  totalPrice,
} from "../commonFuntions/CommonUtilFunctions";

const EXCEL_FILE_PATTERN = /\.(xls|xlsx)$/i;
const YIELD_EVERY_ROWS = 25;

export const quoteExcelAccept =
  ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const yieldToMainThread = () =>
  new Promise((resolve) => {
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => resolve());
      return;
    }

    setTimeout(resolve, 0);
  });

const getFiniteNumber = (value, fallback = 0) => {
  const parsedValue = number(value);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getBeforeTaxValue = (item = {}) =>
  item?.price_before_tax ??
  item?.price_fore_tax ??
  item?.price_pre_tax ??
  item?.before_tax ??
  0;

const getMarginType = (item = {}) => {
  const marginType =
    item?.mob_margin?.margin_type ??
    item?.mob_margin?.amount_in ??
    item?.margin_type ??
    item?.amount_in;

  return String(marginType || "").toLowerCase() === "percentage"
    ? "percentage"
    : "amount";
};

const getMarginValue = (item = {}) =>
  item?.mob_margin?.value ??
  item?.mob_margin?.amount ??
  item?.mob_margin_value ??
  item?.amount ??
  0;

const isPlaceholderRow = (row = {}) =>
  !row?.input &&
  !row?.unit_name &&
  !row?.imageURL &&
  !row?.hsn_sac &&
  !getFiniteNumber(row?.before_tax) &&
  !getFiniteNumber(row?.gst) &&
  !getFiniteNumber(row?.after_tax) &&
  !getFiniteNumber(row?.discount) &&
  !getFiniteNumber(row?.quantity) &&
  !getFiniteNumber(row?.total) &&
  !row?.mob_sku;

const getSanitizedTableMeta = (tableRows = [], fallbackKeyCount = 0) => {
  let hasPlaceholder = false;
  let maxKey = Number(fallbackKeyCount) || 0;
  const sanitizedRows = [];

  for (const row of tableRows) {
    if (isPlaceholderRow(row)) {
      hasPlaceholder = true;
      continue;
    }

    sanitizedRows.push(row);
    maxKey = Math.max(maxKey, Number(row?.key) || 0);
  }

  return {
    rows: hasPlaceholder ? sanitizedRows : tableRows,
    maxKey,
  };
};

const buildImportedRow = (item = {}, key) => {
  const beforeTax = getFiniteNumber(getBeforeTaxValue(item));
  const gst = getFiniteNumber(item?.gst);
  const discount = getFiniteNumber(item?.discount);
  const quantity = getFiniteNumber(item?.quantity);
  const amountIn = getMarginType(item);
  const amount = getFiniteNumber(getMarginValue(item));
  const afterTax =
    item?.price_after_tax !== undefined && item?.price_after_tax !== null
      ? getFiniteNumber(item?.price_after_tax)
      : priceAfterTax({
          before_tax: beforeTax,
          gst,
        });
  const total =
    item?.total !== undefined && item?.total !== null
      ? getFiniteNumber(item?.total)
      : totalPrice({
          before_tax: beforeTax,
          after_tax: afterTax,
          gst,
          discount,
          quantity,
        });

  return {
    key,
    input: item?.product_name || item?.item_name || "",
    unit_name: item?.unit_name || item?.unit || "",
    imageURL: item?.product_image || "",
    hsn_sac: item?.hsn_sac || item?.hsn_code || "",
    before_tax: beforeTax,
    gst,
    after_tax: afterTax,
    amount_in: amountIn,
    amount,
    bmp_price:
      item?.bmp_price !== undefined && item?.bmp_price !== null
        ? getFiniteNumber(item?.bmp_price)
        : "",
    discount,
    quantity,
    total,
    mob_margin: { amount_in: amountIn, amount },
  };
};

export const isExcelFile = (file) => {
  if (!file?.name) {
    return false;
  }

  return EXCEL_FILE_PATTERN.test(file.name);
};

export const extractQuoteExcelPreviewGroups = (response) => {
  const previewData =
    response?.data?.data ??
    response?.data?.preview_data ??
    response?.data?.preview ??
    response?.data ??
    {};

  if (!previewData || typeof previewData !== "object") {
    return [];
  }

  return Object.entries(previewData)
    .map(([index, rows]) => ({
      index: Number(index),
      rows: Array.isArray(rows) ? rows : [],
    }))
    .filter(
      ({ index, rows }) => Number.isInteger(index) && index >= 0 && rows.length
    )
    .sort((firstGroup, secondGroup) => firstGroup.index - secondGroup.index);
};

export const mergeQuoteExcelPreviewData = async ({
  count = [],
  dataSource = [],
  previewGroups = [],
  selectedSeller = [],
  startIndex = 0,
}) => {
  const nextDataSource = [...dataSource];
  const nextSelectedSeller = [...selectedSeller];
  const nextCount = [...count];
  const sanitizedTables = new Map();
  let processedRows = 0;
  const basePreviewIndex = previewGroups?.length ? previewGroups[0].index : 0;

  for (let groupIndex = 0; groupIndex < previewGroups.length; groupIndex += 1) {
    const group = previewGroups[groupIndex];
    const relativeGroupIndex = Math.max(group.index - basePreviewIndex, 0);
    const targetTableIndex = startIndex + relativeGroupIndex;

    while (nextDataSource.length <= targetTableIndex) {
      nextDataSource.push([]);
    }

    while (nextSelectedSeller.length <= targetTableIndex) {
      nextSelectedSeller.push({});
    }

    while (nextCount.length <= targetTableIndex) {
      nextCount.push(0);
    }

    if (!sanitizedTables.has(targetTableIndex)) {
      sanitizedTables.set(
        targetTableIndex,
        getSanitizedTableMeta(
          nextDataSource[targetTableIndex] || [],
          nextCount[targetTableIndex]
        )
      );
    }

    const tableMeta = sanitizedTables.get(targetTableIndex);
    const existingRows = tableMeta?.rows || [];
    let nextKey = tableMeta?.maxKey || 0;
    const importedRows = [];

    for (const row of group.rows) {
      nextKey += 1;
      importedRows.push(buildImportedRow(row, nextKey));
      processedRows += 1;

      if (processedRows % YIELD_EVERY_ROWS === 0) {
        await yieldToMainThread();
      }
    }

    nextDataSource[targetTableIndex] = [...existingRows, ...importedRows];
    sanitizedTables.set(targetTableIndex, {
      rows: nextDataSource[targetTableIndex],
      maxKey: nextKey,
    });
    nextSelectedSeller[targetTableIndex] =
      nextSelectedSeller[targetTableIndex] || {};
    nextCount[targetTableIndex] = nextKey;
  }

  return {
    count: nextCount,
    dataSource: nextDataSource,
    selectedSeller: nextSelectedSeller,
  };
};
